import React, { useState } from "react";
import { deletePhotoByUrl, uploadTapeAdditionalPhoto, uploadTapeMainPhoto } from "../lib/storage";
import { addTape, updateTape } from "../lib/tapes";
import { T } from "../theme";
import { Btn, Card, CONDITIONS, COUNTRIES, GENRES, Input, Select, SectionTitle, TapeIcon } from "../ui";

const emptyForm = {
  artist: "", albumTitle: "", releaseYear: "", recordLabel: "", catalogNumber: "",
  genre: "Rock", condition: "Unknown", cartridgeColor: "", variation: "",
  countryOfManufacture: "Unknown", countryOfRelease: "Unknown",
  inherited: false, purchasePrice: "", estimatedValue: "", purchaseDate: "", purchaseSource: "",
  trackListing: "", notes: "",
};

export default function TapeFormScreen({ ownerUserId, tape, tier, onDone, onCancel, onLimitReached }) {
  const [form, setForm] = useState(() =>
    tape
      ? {
          artist: tape.artist || "", albumTitle: tape.albumTitle || "",
          releaseYear: tape.releaseYear || "", recordLabel: tape.recordLabel || "",
          catalogNumber: tape.catalogNumber || "", genre: tape.genre || "Rock",
          condition: tape.condition || "Unknown", cartridgeColor: tape.cartridgeColor || "",
          variation: tape.variation || "",
          countryOfManufacture: tape.countryOfManufacture || "Unknown",
          countryOfRelease: tape.countryOfRelease || "Unknown",
          inherited: !!tape.inherited, purchasePrice: tape.purchasePrice ?? "",
          estimatedValue: tape.estimatedValue ?? "", purchaseDate: tape.purchaseDate || "",
          purchaseSource: tape.purchaseSource || "",
          trackListing: (tape.trackListing || []).join("\n"), notes: tape.notes || "",
        }
      : emptyForm
  );
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(tape?.mainPhotoUrl || null);
  // Each entry: { url, file } — file is set for a not-yet-uploaded photo, url for an
  // already-saved one (or the object-preview URL of a pending file).
  const [additionalPhotos, setAdditionalPhotos] = useState(
    () => (tape?.photoUrls || []).map((url) => ({ url, file: null }))
  );
  const [removedUrls, setRemovedUrls] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const totalPhotoCount = (photoPreview ? 1 : 0) + additionalPhotos.length;

  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const onAddAdditionalPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (totalPhotoCount >= tier.photoLimit) {
      onLimitReached(`You've reached the ${tier.name} limit of ${tier.photoLimit} photo${tier.photoLimit === 1 ? "" : "s"} per tape. Upgrade to add more.`);
      return;
    }
    setAdditionalPhotos((prev) => [...prev, { url: URL.createObjectURL(file), file }]);
  };

  const removeAdditionalPhoto = (index) => {
    const item = additionalPhotos[index];
    if (item.file === null) setRemovedUrls((prev) => [...prev, item.url]);
    setAdditionalPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    setError("");
    if (!form.artist || !form.albumTitle) {
      setError("Artist and Album/Title are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        artist: form.artist,
        albumTitle: form.albumTitle,
        releaseYear: form.releaseYear ? Number(form.releaseYear) : null,
        recordLabel: form.recordLabel || null,
        catalogNumber: form.catalogNumber || null,
        genre: form.genre || null,
        condition: form.condition || null,
        cartridgeColor: form.cartridgeColor || null,
        variation: form.variation || null,
        countryOfManufacture: form.countryOfManufacture || null,
        countryOfRelease: form.countryOfRelease || null,
        inherited: !!form.inherited,
        purchasePrice: form.inherited ? null : (form.purchasePrice !== "" ? Number(form.purchasePrice) : null),
        estimatedValue: form.estimatedValue !== "" ? Number(form.estimatedValue) : null,
        currencyCode: "USD",
        purchaseDate: form.purchaseDate || null,
        purchaseSource: form.purchaseSource || null,
        trackListing: form.trackListing.split("\n").map((t) => t.trim()).filter(Boolean),
        notes: form.notes || null,
        isFavorite: tape?.isFavorite || false,
      };

      let tapeId = tape?.id;
      if (tapeId) {
        await updateTape(tapeId, payload);
      } else {
        const docRef = await addTape(ownerUserId, { ...payload, mainPhotoUrl: null, photoUrls: [] });
        tapeId = docRef.id;
      }

      if (photoFile) {
        const url = await uploadTapeMainPhoto(ownerUserId, tapeId, photoFile);
        await updateTape(tapeId, { mainPhotoUrl: url });
      }

      await Promise.all(removedUrls.map((url) => deletePhotoByUrl(url)));

      const finalPhotoUrls = await Promise.all(
        additionalPhotos.map((item) =>
          item.file ? uploadTapeAdditionalPhoto(ownerUserId, tapeId, item.file) : item.url
        )
      );
      await updateTape(tapeId, { photoUrls: finalPhotoUrls });

      onDone();
    } catch (e) {
      setError("Could not save this tape. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Card style={{ background: T.surface, textAlign: "center", padding: "20px 14px" }}>
        {photoPreview ? (
          <img src={photoPreview} alt="Tape cover" style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 8, border: `1px solid ${T.border}` }} />
        ) : (
          <TapeIcon size={64} />
        )}
        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 10, color: T.accent, cursor: "pointer", fontWeight: "bold", letterSpacing: "0.1em" }}>
            {photoPreview ? "Change Main Photo" : "Add Main Photo"}
            <input type="file" accept="image/*" onChange={onPhotoChange} style={{ display: "none" }} />
          </label>
        </div>
      </Card>

      {tier.photoLimit > 1 && (
        <>
          <SectionTitle>Additional Photos ({totalPhotoCount}/{tier.photoLimit === Infinity ? "∞" : tier.photoLimit})</SectionTitle>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {additionalPhotos.map((item, i) => (
              <div key={i} style={{ position: "relative", width: 64, height: 64 }}>
                <img src={item.url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${T.border}` }} />
                <button onClick={() => removeAdditionalPhoto(i)} style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: 10, background: "#C0392B", color: "#fff", border: "none", cursor: "pointer", fontSize: 12, lineHeight: 1 }}>×</button>
              </div>
            ))}
            {totalPhotoCount < tier.photoLimit && (
              <label style={{ width: 64, height: 64, borderRadius: 8, border: `1px dashed ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.muted, fontSize: 22 }}>
                +
                <input type="file" accept="image/*" onChange={onAddAdditionalPhoto} style={{ display: "none" }} />
              </label>
            )}
          </div>
        </>
      )}

      {error && <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <SectionTitle>Basic Information</SectionTitle>
      <Input label="Album / Title *" value={form.albumTitle} onChange={set("albumTitle")} placeholder="e.g. Rumours" />
      <Input label="Artist *" value={form.artist} onChange={set("artist")} placeholder="e.g. Fleetwood Mac" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Input label="Release Year" value={form.releaseYear} onChange={set("releaseYear")} placeholder="1977" type="number" />
        <Input label="Record Label" value={form.recordLabel} onChange={set("recordLabel")} placeholder="Warner Bros." />
      </div>
      <Input label="Catalog Number" value={form.catalogNumber} onChange={set("catalogNumber")} placeholder="Optional" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Select label="Genre" value={form.genre} onChange={set("genre")} options={GENRES} />
        <Select label="Condition" value={form.condition} onChange={set("condition")} options={CONDITIONS} />
      </div>

      <SectionTitle>Physical Details</SectionTitle>
      <Input label="Cartridge Color" value={form.cartridgeColor} onChange={set("cartridgeColor")} placeholder="Black, Brown, Cream…" />
      <Input label="Tape / Label Variation" value={form.variation} onChange={set("variation")} placeholder="e.g. reissue, promo pressing" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Select label="Country of Manufacture" value={form.countryOfManufacture} onChange={set("countryOfManufacture")} options={COUNTRIES} />
        <Select label="Country of Release" value={form.countryOfRelease} onChange={set("countryOfRelease")} options={COUNTRIES} />
      </div>

      <SectionTitle>Purchase and Value</SectionTitle>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={form.inherited}
          onChange={(e) => setForm({ ...form, inherited: e.target.checked, purchasePrice: e.target.checked ? "" : form.purchasePrice })}
        />
        <span style={{ fontSize: 12, color: T.text }}>Inherited / received as a gift (no purchase price)</span>
      </label>
      <div style={{ display: "grid", gridTemplateColumns: form.inherited ? "1fr" : "1fr 1fr", gap: 10 }}>
        {!form.inherited && (
          <Input label="Purchase Price ($)" value={form.purchasePrice} onChange={set("purchasePrice")} placeholder="0.00" type="number" />
        )}
        <Input label="Estimated Value ($)" value={form.estimatedValue} onChange={set("estimatedValue")} placeholder="0.00" type="number" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Input label="Purchase Date" value={form.purchaseDate} onChange={set("purchaseDate")} type="date" />
        <Input label={form.inherited ? "Received From" : "Seller / Source"} value={form.purchaseSource} onChange={set("purchaseSource")} placeholder={form.inherited ? "e.g. Grandma's collection" : "Store, seller, gift…"} />
      </div>

      <SectionTitle>Tracks and Notes</SectionTitle>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: 3 }}>Track / Program Listing (one per line)</label>
        <textarea value={form.trackListing} onChange={set("trackListing")} style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "9px 11px", fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box", minHeight: 80, resize: "vertical" }} />
      </div>
      <Input label="Notes" value={form.notes} onChange={set("notes")} placeholder="First pressing, special edition…" />

      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        <Btn variant="outline" onClick={onCancel} style={{ flex: 1 }}>Cancel</Btn>
        <Btn disabled={saving} onClick={save} style={{ flex: 2 }}>{saving ? "Saving…" : "Save Tape"}</Btn>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
